import React, { useState, useEffect } from 'react';
import { X, Search, Package, MapPin, Clock, Save, RotateCcw, CheckCircle, AlertCircle, Calendar, User, Navigation } from 'lucide-react';
import '../styles/ConsignmentEntryFormLT.css';
import apiClient from '../services/api';

const ConsignmentStatusEntry = ({ onClose }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Search state
  const [awbSearch, setAwbSearch] = useState('');
  const [consignmentData, setConsignmentData] = useState(null);
  const [showSelectionList, setShowSelectionList] = useState(false);
  const [consignmentList, setConsignmentList] = useState([]);
  
  // Status history
  const [statusHistory, setStatusHistory] = useState([]);
  
  // Dropdown data
  const [courierStatuses, setCourierStatuses] = useState([]);
  
  // Status entry form
  const [statusForm, setStatusForm] = useState({
    StatusDate: new Date().toISOString().slice(0, 16),
    CourierStatusID: '',
    Location: '',
    Remarks: ''
  });

  // Session
  const session = getSessionData();

  function getSessionData() {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session:', error);
      return null;
    }
  }

  // Safe close handler - defined early so useEffect can use it
  const handleClose = () => {
    console.log('🚪 Close button clicked');
    if (typeof onClose === 'function') {
      console.log('✓ Calling onClose()');
      onClose();
    } else {
      console.log('⚠️ No onClose function - navigating to dashboard');
      window.location.href = '/dashboard';  // ✅ Go to dashboard
    }
  };

  useEffect(() => {
    loadDropdownData();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showSelectionList) {
          setShowSelectionList(false);
        } else {
          handleClose();
        }
      }
      if (e.key === 'F6') {
        e.preventDefault();
        handleSearch();
      }
      if (e.key === 'F5') {
        e.preventDefault();
        if (consignmentData) handleSaveStatus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showSelectionList, consignmentData, statusForm, onClose]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDropdownData = async () => {
    try {
      const statusRes = await apiClient.get('/CourierStatus.php').catch(e => ({ data: { success: false } }));
      if (statusRes.data.success) {
        setCourierStatuses(statusRes.data.data || []);
      }
    } catch (err) {
      console.error('Error loading dropdown data:', err);
    }
  };

  const handleSearch = async () => {
    if (!awbSearch.trim()) {
      setError('Please enter AWB/Consignment number');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      console.log('🔍 Searching for:', awbSearch);

      // Phase 1: Try EXACT AWB match
      console.log('Phase 1: Trying exact match...');
      const exactResponse = await apiClient.get(`/ConsignmentSearch.php?awb=${awbSearch}`);
      
      console.log('Exact search response:', exactResponse.data);

      if (exactResponse.data.success && exactResponse.data.data) {
        // Single exact match found
        const consignment = exactResponse.data.data;
        console.log('✅ Exact match found:', consignment.ConsignmentNo);
        setConsignmentData(consignment);
        loadStatusHistory(consignment.ConsignmentID);
        setStatusForm(prev => ({
          ...prev,
          Location: consignment.DestinationCity || consignment.DestinationCountry || ''
        }));
        setError('');
        setLoading(false);
        return;
      }

      console.log('⚠️ No exact match, trying partial search...');

      // Phase 2: Partial search
      const partialResponse = await apiClient.get(`/ConsignmentSearch.php?search=${awbSearch}`);
      
      console.log('Partial search response:', partialResponse.data);

      if (partialResponse.data.success && partialResponse.data.count > 0) {
        const results = partialResponse.data.data;
        console.log('📊 Total results:', results.length);

        if (results.length === 1) {
          console.log('✅ Single result, loading directly');
          const result = results[0];
          setConsignmentData(result);
          loadStatusHistory(result.ConsignmentID);
          setStatusForm(prev => ({
            ...prev,
            Location: result.DestinationCity || result.DestinationCountry || ''
          }));
        } else {
          console.log('📋 Multiple results (' + results.length + '), showing selection list');
          setConsignmentList(results);
          setShowSelectionList(true);
          console.log('showSelectionList set to:', true);
        }
      } else {
        setError('No consignments found matching "' + awbSearch + '"');
        setConsignmentData(null);
        setStatusHistory([]);
      }
    } catch (err) {
      console.error('❌ Search error:', err);
      setError('Error searching: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const loadStatusHistory = async (consignmentId) => {
    console.log('📜 Loading status history for consignment:', consignmentId);
    
    if (!consignmentId) {
      console.error('❌ No consignment ID provided!');
      setStatusHistory([]);
      return;
    }
    
    try {
      const url = `/ConsignmentStatusHistory.php?consignmentid=${consignmentId}`;
      console.log('📡 API URL:', url);
      
      const response = await apiClient.get(url);
      console.log('📥 Status history response:', response);
      console.log('📦 Response data:', response.data);
      
      if (response.data.success) {
        const history = response.data.data || [];
        console.log('✅ Status history loaded:', history.length, 'entries');
        
        if (history.length > 0) {
          console.log('📋 First entry:', history[0]);
        }
        
        setStatusHistory(history);
      } else {
        console.log('⚠️ API returned success=false:', response.data.message);
        setStatusHistory([]);
        setError('Could not load status history: ' + response.data.message);
      }
    } catch (err) {
      console.error('❌ Error loading status history:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setStatusHistory([]);
      setError('Error loading status history. Check console for details.');
    }
  };

  const handleSelectConsignment = (consignment) => {
    console.log('✅ Selected:', consignment.ConsignmentNo);
    setConsignmentData(consignment);  // Type already included in data
    loadStatusHistory(consignment.ConsignmentID);
    setStatusForm(prev => ({
      ...prev,
      Location: consignment.DestinationCity || consignment.DestinationCountry || ''
    }));
    setShowSelectionList(false);
  };

  const handleStatusFormChange = (e) => {
    const { name, value } = e.target;
    setStatusForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveStatus = async () => {
    if (!consignmentData) {
      setError('No consignment loaded');
      return;
    }

    if (!statusForm.CourierStatusID) {
      setError('Please select a status');
      return;
    }

    if (!statusForm.StatusDate) {
      setError('Status date is required');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const dataToSave = {
        ConsignmentID: consignmentData.ConsignmentID,
        CourierStatusID: statusForm.CourierStatusID,
        StatusDate: statusForm.StatusDate,
        Location: statusForm.Location || null,
        Remarks: statusForm.Remarks || null
      };

      const response = await apiClient.post('/ConsignmentStatusHistory.php', dataToSave);

      if (response.data.success) {
        setSuccess('Status updated successfully!');
        loadStatusHistory(consignmentData.ConsignmentID);
        setStatusForm({
          StatusDate: new Date().toISOString().slice(0, 16),
          CourierStatusID: '',
          Location: statusForm.Location,
          Remarks: ''
        });
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(response.data.message || 'Failed to save status');
      }
    } catch (err) {
      console.error('Error saving status:', err);
      setError('Error saving: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => {
    setAwbSearch('');
    setConsignmentData(null);
    setStatusHistory([]);
    setStatusForm({
      StatusDate: new Date().toISOString().slice(0, 16),
      CourierStatusID: '',
      Location: '',
      Remarks: ''
    });
    setError('');
    setSuccess('');
    setShowSelectionList(false);
    setConsignmentList([]);
  };

  if (!session?.companyid) {
    return (
      <div className="consignment-form-overlay">
        <div className="consignment-form-container" style={{ maxWidth: '600px' }}>
          <div style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            padding: '28px 32px',
            borderRadius: '12px 12px 0 0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h2 style={{ color: 'white', margin: 0 }}>Session Required</h2>
            <button 
              onClick={handleClose}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: '8px'
              }}
            >
              <X size={24} />
            </button>
          </div>
          <div style={{ padding: '24px', textAlign: 'center' }}>
            <p>Please select a company and financial year from the dashboard.</p>
            <button 
              onClick={handleClose}
              className="btn btn-primary" 
              style={{ marginTop: '16px' }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="consignment-form-overlay">
      <div className="consignment-form-container" style={{ maxWidth: '1200px' }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
          padding: '28px 32px',
          borderRadius: '12px 12px 0 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '4px solid #2563eb',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flex: 1 }}>
            <div style={{
              width: '60px',
              height: '60px',
              background: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
            }}>
              <Navigation size={24} color="#ffffff" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ 
                color: '#ffffff',
                fontSize: '1.8rem',
                fontWeight: '700',
                textShadow: '0 2px 4px rgba(0,0,0,0.5)'
              }}>
                Consignment Status Entry
              </div>
              <div style={{ 
                color: 'rgba(255,255,255,0.9)',
                fontSize: '0.95rem',
                marginTop: '4px'
              }}>
                Track and update consignment status
              </div>
            </div>
          </div>
          {/* ✅ ULTRA SIMPLE CLOSE BUTTON */}
          <button 
            onClick={handleClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              color: '#ffffff',
              width: '44px',
              height: '44px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.35)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Toolbar */}
        <div style={{ 
          padding: '16px 30px', 
          background: '#f9fafb', 
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          gap: '12px'
        }}>
          <button 
            type="button" 
            onClick={handleSearch} 
            disabled={loading}
            style={{
              padding: '8px 16px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontWeight: '500',
              opacity: loading ? 0.6 : 1
            }}
          >
            <Search size={18} /> Search (F6)
          </button>
          <button 
            type="button" 
            onClick={handleSaveStatus} 
            disabled={saving || !consignmentData}
            style={{
              padding: '8px 16px',
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontWeight: '500',
              opacity: (saving || !consignmentData) ? 0.6 : 1
            }}
          >
            <Save size={18} /> Save Status (F5)
          </button>
          <button 
            type="button" 
            onClick={handleClear} 
            disabled={loading}
            style={{
              padding: '8px 16px',
              background: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontWeight: '500',
              opacity: loading ? 0.6 : 1
            }}
          >
            <RotateCcw size={18} /> Clear
          </button>
        </div>

        {/* Alert Messages */}
        {error && (
          <div className="alert alert-error" style={{ margin: '16px 30px 0' }}>
            <AlertCircle size={20} />
            <strong>Error:</strong> {error}
          </div>
        )}

        {success && (
          <div className="alert" style={{ 
            margin: '16px 30px 0',
            background: '#d1fae5',
            border: '1px solid #6ee7b7',
            color: '#065f46'
          }}>
            <CheckCircle size={20} />
            <strong>Success:</strong> {success}
          </div>
        )}

        <div className="form-content">
          {/* Search Section */}
          <div className="form-section" style={{ marginBottom: '16px' }}>
            <h3 className="section-title">
              <Search size={18} />
              Search Consignment
            </h3>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label>AWB / Consignment Number</label>
                <input
                  type="text"
                  value={awbSearch}
                  onChange={(e) => setAwbSearch(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Enter consignment number (e.g., CNS-LT-000001)"
                  disabled={loading}
                  autoFocus
                  style={{ width: '100%' }}
                />
              </div>
              <button
                type="button"
                onClick={handleSearch}
                disabled={loading}
                style={{
                  padding: '10px 20px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontWeight: '500',
                  height: '42px'
                }}
              >
                <Search size={18} />
                {loading ? 'Searching...' : 'Search'}
              </button>
            </div>
          </div>

          {/* ✅ Selection Modal with detailed logging */}
          {showSelectionList && (
            <div 
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10000
              }} 
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  console.log('🚪 Closing selection list (overlay click)');
                  setShowSelectionList(false);
                }
              }}
            >
              <div style={{
                background: 'white',
                borderRadius: '12px',
                maxWidth: '900px',
                width: '90%',
                maxHeight: '80vh',
                overflow: 'auto'
              }}>
                <div style={{
                  padding: '20px',
                  borderBottom: '2px solid #e5e7eb',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Package size={20} />
                    Select Consignment ({consignmentList.length} found)
                  </h3>
                  <button 
                    onClick={() => {
                      console.log('🚪 Closing selection list (X button)');
                      setShowSelectionList(false);
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '8px'
                    }}
                  >
                    <X size={20} />
                  </button>
                </div>
                <div style={{ padding: '20px' }}>
                  <table className="data-table" style={{ width: '100%' }}>
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Consignment No</th>
                        <th>Date</th>
                        <th>Shipper</th>
                        <th>Consignee</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {consignmentList.map((cons) => (
                        <tr key={cons.ConsignmentID}>
                          <td>
                            <span style={{
                              background: cons.Type === 'LT' ? '#ddd6fe' : 
                                         cons.Type === 'D' ? '#d1fae5' : 
                                         '#fef3c7',
                              color: cons.Type === 'LT' ? '#5b21b6' : 
                                     cons.Type === 'D' ? '#065f46' : 
                                     '#92400e',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '0.85rem',
                              fontWeight: '600'
                            }}>
                              {cons.Type}
                            </span>
                          </td>
                          <td style={{ fontFamily: 'monospace', fontWeight: '600' }}>{cons.ConsignmentNo}</td>
                          <td>{new Date(cons.ConsignmentDate).toLocaleDateString()}</td>
                          <td>{cons.ShipperName || cons.Shipper || '-'}</td>
                          <td>{cons.ConsigneeName || cons.Consignee || '-'}</td>
                          <td>{cons.Status || 'Pending'}</td>
                          <td>
                            <button
                              onClick={() => handleSelectConsignment(cons)}
                              style={{
                                padding: '6px 12px',
                                background: '#3b82f6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                fontWeight: '500'
                              }}
                            >
                              Select
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Consignment Details */}
          {consignmentData && (
            <div className="form-section" style={{ marginBottom: '16px' }}>
              <h3 className="section-title">
                <Package size={18} />
                Consignment Details
              </h3>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                gap: '16px',
                background: '#f9fafb',
                padding: '16px',
                borderRadius: '8px'
              }}>
                <div>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '4px' }}>Consignment No</div>
                  <div style={{ fontWeight: '600', fontFamily: 'monospace' }}>{consignmentData.ConsignmentNo}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '4px' }}>Date</div>
                  <div style={{ fontWeight: '600' }}>{new Date(consignmentData.ConsignmentDate).toLocaleDateString()}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '4px' }}>Type</div>
                  <div>
                    <span style={{
                      background: consignmentData.Type === 'LT' ? '#ddd6fe' : 
                                 consignmentData.Type === 'D' ? '#d1fae5' : 
                                 '#fef3c7',
                      color: consignmentData.Type === 'LT' ? '#5b21b6' : 
                             consignmentData.Type === 'D' ? '#065f46' : 
                             '#92400e',
                      padding: '4px 12px',
                      borderRadius: '4px',
                      fontSize: '0.85rem',
                      fontWeight: '600'
                    }}>
                      {consignmentData.Type === 'LT' ? 'Land Transport' : 
                       consignmentData.Type === 'D' ? 'Domestic' : 
                       'International'}
                    </span>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '4px' }}>Shipper</div>
                  <div style={{ fontWeight: '600' }}>{consignmentData.ShipperName || consignmentData.Shipper}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '4px' }}>Consignee</div>
                  <div style={{ fontWeight: '600' }}>{consignmentData.ConsigneeName || consignmentData.Consignee}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '4px' }}>Route</div>
                  <div style={{ fontWeight: '600' }}>
                    {consignmentData.OriginCity || consignmentData.OriginCountry || 'N/A'} → {consignmentData.DestinationCity || consignmentData.DestinationCountry || 'N/A'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '4px' }}>Weight</div>
                  <div style={{ fontWeight: '600' }}>{consignmentData.Weight || 0} kg</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '4px' }}>Pieces</div>
                  <div style={{ fontWeight: '600' }}>{consignmentData.Pieces || 0}</div>
                </div>
              </div>
            </div>
          )}

          {/* Status Entry Form */}
          {consignmentData && (
            <div className="form-section" style={{ marginBottom: '16px' }}>
              <h3 className="section-title" style={{ background: '#dbeafe', padding: '8px 12px', borderRadius: '6px' }}>
                <Navigation size={18} />
                Add New Status Update
              </h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Status Date & Time *</label>
                  <input
                    type="datetime-local"
                    name="StatusDate"
                    value={statusForm.StatusDate}
                    onChange={handleStatusFormChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Status *</label>
                  <select
                    name="CourierStatusID"
                    value={statusForm.CourierStatusID}
                    onChange={handleStatusFormChange}
                    required
                  >
                    <option value="">-- Select Status --</option>
                    {courierStatuses.map(status => (
                      <option key={status.CourierStatusID} value={status.CourierStatusID}>
                        {status.StatusName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Location</label>
                  <input
                    type="text"
                    name="Location"
                    value={statusForm.Location}
                    onChange={handleStatusFormChange}
                    placeholder="Current location"
                  />
                </div>
                <div className="form-group full-width">
                  <label>Remarks</label>
                  <textarea
                    name="Remarks"
                    value={statusForm.Remarks}
                    onChange={handleStatusFormChange}
                    rows="2"
                    placeholder="Additional notes about this status update..."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Status History */}
          {consignmentData && (
            <div className="form-section" style={{ marginBottom: '16px' }}>
              <h3 className="section-title">
                <Clock size={18} />
                Status History ({statusHistory.length} {statusHistory.length === 1 ? 'update' : 'updates'})
              </h3>
              
              {statusHistory.length === 0 ? (
                <div style={{ 
                  padding: '32px', 
                  textAlign: 'center',
                  background: '#f9fafb',
                  borderRadius: '8px',
                  color: '#6b7280'
                }}>
                  <Clock size={48} style={{ opacity: 0.3, marginBottom: '12px' }} />
                  <p style={{ margin: 0, fontSize: '0.95rem' }}>No status history yet</p>
                  <p style={{ margin: '8px 0 0 0', fontSize: '0.85rem' }}>Add a status update above to start tracking</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="data-table" style={{ width: '100%' }}>
                    <thead>
                      <tr>
                        <th style={{ width: '50px' }}>#</th>
                        <th>Date & Time</th>
                        <th>Status</th>
                        <th>Location</th>
                        <th>Remarks</th>
                        <th>Updated By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statusHistory.map((status, index) => (
                        <tr key={index}>
                          <td style={{ textAlign: 'center', fontWeight: '600', color: '#6b7280' }}>
                            {index + 1}
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Calendar size={14} />
                              {new Date(status.StatusDate).toLocaleString()}
                            </div>
                          </td>
                          <td>
                            <span style={{
                              background: '#dbeafe',
                              color: '#1e40af',
                              padding: '4px 10px',
                              borderRadius: '4px',
                              fontSize: '0.85rem',
                              fontWeight: '600'
                            }}>
                              {status.StatusName || status.Status}
                            </span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <MapPin size={14} />
                              {status.Location || '-'}
                            </div>
                          </td>
                          <td>{status.Remarks || '-'}</td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <User size={14} />
                              {status.UpdatedByName || '-'}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="form-footer">
          <button
            type="button"
            className="btn btn-cancel"
            onClick={handleClose}
            disabled={loading || saving}
          >
            Close
          </button>
          <button
            type="button"
            className="btn btn-save"
            onClick={handleSaveStatus}
            disabled={saving || !consignmentData}
            style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}
          >
            {saving ? (
              <>
                <div className="btn-spinner" />
                Saving...
              </>
            ) : (
              <>
                <Save size={18} />
                Save Status Update
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsignmentStatusEntry;